import type {Integration} from "@sentry/core";

import {kindErrorDataIntegration} from "@khanacademy/wonder-stuff-sentry";
import {
    __InternalGetUserAttributes,
    DeployInfo,
    RequestInfo,
} from "@khan/shared-core";

type Sentry = typeof import("@sentry/browser");

export type {Integration};

export interface SentryExtraConfig {
    enabled: boolean;
    forceNoSample: boolean;
    integrations?: Array<Integration>;
    dsn: string;
}

let _Sentry: any | null | undefined = null;
let sentryExtraConfig: SentryExtraConfig | null | undefined = null;

/**
 * Clear the Sentry setup.
 *
 * This is a no-op outside of tests.
 */
export const clearSentryTestOnly = () => {
    if (process.env.NODE_ENV === "test") {
        _Sentry = null;
        sentryExtraConfig = null;
    }
};

/**
 * Initializes Sentry.
 *
 * NOTE: This should only happen once. (see ./site-infra.js)
 *
 * @param {@sentry/browser} Sentry The Sentry's module
 */
export const configSentry = (Sentry: Sentry) => {
    if (RequestInfo.isUnsupportedBrowser) {
        // If we're running an unsupported browser, we expect some errors and
        // don't want nor need to record them to Sentry; just bail out
        return;
    }

    _Sentry = Sentry;

    const extraConfig = sentryExtraConfig;
    // If the extra Sentry config hasn't loaded yet, or if it's not enabled,
    // then we bail at this point.
    if (!extraConfig?.enabled) {
        return;
    }

    if (!Sentry.isInitialized()) {
        Sentry.init({
            // This tells the Sentry SDK where to send the events.
            // See https://khanacademy.atlassian.net/wiki/spaces/ENG/pages/497910118/Debugging+Sentry
            dsn:
                RequestInfo.IS_DEV_SERVER ||
                DeployInfo.staticVersion.startsWith("znd")
                    ? // Development and ZNDs => sourcemap-test
                      "https://ff8ead3bfa9a4c5b9987f5fd8c62301b@o8287.ingest.sentry.io/1281302"
                    : extraConfig.dsn,
            release: DeployInfo.staticVersion
                ? DeployInfo.staticVersion
                : "development",
            // Used to make sure `.captureMessage` errors still have a stacktrace
            attachStacktrace: true,

            // By default we only sample 1% of all requests, however we will
            // increase that to 100% if:
            //     1. we're in a certain entrypoint (such as devadmin)
            //        This helps to ensure that we don't miss errors on these
            //        relatively low-traffic pages.
            //     2. we override it by providing ?devflagk=noSentrySample
            sampleRate: extraConfig?.forceNoSample ? 1.0 : 0.01,

            ignoreErrors: [
                // Random plugins/extensions
                "top.GLOBALS",
                // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
                "originalCreateNotification",
                "canvas.contentDocument",
                "MyApp_RemoveAllHighlights",
                "http://tt.epicplay.com",
                "Can't find variable: ZiteReader",
                "jigsaw is not defined",
                "ComboSearch is not defined",
                "http://loading.retry.widdit.com/",
                "atomicFindClose",
                // Facebook borked
                "fb_xd_fragment",
                // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
                // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
                "bmi_SafeAddOnload",
                "EBCallBackMessageReceived",
                // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
                "conduitPage",
                // Common errors not in our code
                "document.getElementsByClassName.ToString is not a function",
                "Can't find variable: didEnterViewPort",
                "u3HU.data.indexOf is not a function",
                "Cannot find function race in object function Promise() { [native code] }",
                // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
                "ResizeObserver loop limit exceeded",
            ],
            denyUrls: [
                // Facebook flakiness
                /graph\.facebook\.com/i,
                // Facebook blocked
                /connect\.facebook\.net\/en_US\/all\.js/i,
                // Woopra flakiness
                /eatdifferent\.com\.woopra-ns\.com/i,
                /static\.woopra\.com\/js\/woopra\.js/i,
                // Chrome extensions
                /extensions\//i,
                /^chrome:\/\//i,
                /^chrome-extension:\/\//i,
                // Other plugins
                /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
                /webappstoolbarba\.texthelp\.com\//i,
                /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
            ],
            integrations: [
                // This integration ensures additional metadata is included from
                // KindError-based and KindSentryError-based errors.
                kindErrorDataIntegration(),
                ...(extraConfig.integrations ?? []),
            ],
            // This means we will send a bit more depth in our context data as the
            // default of 3 is a bit shallow.
            normalizeDepth: 5,
        });
    }
    const userAttributes = __InternalGetUserAttributes();
    if (userAttributes) {
        Sentry.setUser({id: userAttributes.kaid});
    }
};

export const configExtraSentry = (config: SentryExtraConfig) => {
    sentryExtraConfig = config;

    // If the Sentry object already exists, that means we already initialized.
    // We're going to want to apply this new config to it.
    if (_Sentry) {
        configSentry(_Sentry);
    }
};
